import { ANNOUNCE_WIN_MS, COUNTDOWN_GO_MS, PREVIOUS_RESULT_THRESHOLD_MS } from './constants';
import { getLiveDrawBallsEndMs, getLiveDrawImmediateWinStartMs, resolveLiveDrawBallReveal } from './getLiveDrawRevealTiming';

type LiveDrawSlot =
  /** More than 2h until sales close — the tab shows the previous draw's result. */
  | { kind: 'previous_result' }
  /** Sales still open; header counts down to the closing date alongside a marketing banner. */
  | { kind: 'selling_countdown' }
  /** Sales closed, draw not yet started — "Game is about to start" for the ~5 minute gap. */
  | { kind: 'draw_starting' }
  /** 3 / 2 / 1 / Go over the number grid. */
  | { kind: 'countdown_go' }
  /** Balls landing one by one; `revealedCount` is how many have landed so far. */
  | { kind: 'drawing_numbers'; revealedCount: number }
  /**
   * Numbers are out, the player holds a ticket, and the prize has not been scored yet.
   * Holds until evaluation lands, then yields to `announce_win` or `complete`.
   */
  | { kind: 'calculating_win' }
  | { kind: 'announce_win' }
  /** Terminal: completed slip, held until the payload rolls to the next drawing. */
  | { kind: 'complete' };

type LiveDrawStage = {
  slot: LiveDrawSlot;
  /** Which instant the header ticks towards: "Draw closes in", "Live draw in", or neither. */
  countdownTarget: 'closing' | 'drawing' | null;
};

type ResolveLiveDrawStageInput = Readonly<{
  /** `closingDate - now`; negative once sales have closed. */
  msUntilClose: number;
  /** `drawingDate - now`; negative once the draw has started. */
  msUntilDraw: number;
  /** How many balls to reveal — main numbers plus extras; 0 until the draw has been resulted. */
  drawnNumberCount: number;
  hasWinningTicket: boolean;
  /**
   * The player holds a ticket in this drawing and the prize has not been scored yet.
   * Distinct from a confirmed loss (`hasWinningTicket: false` with evaluation done).
   */
  isAwaitingEvaluation?: boolean;
  /**
   * Sequence elapsed time at which the win board should start, measured from the same origin as
   * the draw clock. Defaults to one second after the last ball when the prize is already known.
   * A prize that lands later passes the moment it landed, so the board starts then — with no
   * extra pause — and still gets a full {@link ANNOUNCE_WIN_MS}.
   */
  announceWinStartElapsedMs?: number;
}>;

/**
 * Maps wall-clock position within a drawing onto the live draw stage to render.
 *
 * Purely derived from the two dates, with no elapsed-since-mount state, so landing on the page
 * mid-draw shows the correct stage rather than restarting (WEBD-918; BRD FR-RP-05) and a finished
 * draw stays finished instead of looping.
 */
function resolveLiveDrawStage({
  msUntilClose,
  msUntilDraw,
  drawnNumberCount,
  hasWinningTicket,
  isAwaitingEvaluation = false,
  announceWinStartElapsedMs,
}: ResolveLiveDrawStageInput): LiveDrawStage {
  if (msUntilClose > PREVIOUS_RESULT_THRESHOLD_MS) {
    return { slot: { kind: 'previous_result' }, countdownTarget: null };
  }

  if (msUntilClose > 0) {
    return { slot: { kind: 'selling_countdown' }, countdownTarget: 'closing' };
  }

  if (msUntilDraw > 0) {
    return { slot: { kind: 'draw_starting' }, countdownTarget: 'drawing' };
  }

  const elapsedMs = -msUntilDraw;

  if (elapsedMs < COUNTDOWN_GO_MS) {
    return { slot: { kind: 'countdown_go' }, countdownTarget: null };
  }

  // Results can lag the scheduled draw; hold on an empty grid rather than skipping to the terminal state.
  if (drawnNumberCount === 0) {
    return { slot: { kind: 'drawing_numbers', revealedCount: 0 }, countdownTarget: null };
  }

  const ballsEndMs = getLiveDrawBallsEndMs(drawnNumberCount);

  if (elapsedMs < ballsEndMs) {
    const reveal = resolveLiveDrawBallReveal(elapsedMs, drawnNumberCount);

    return { slot: { kind: 'drawing_numbers', revealedCount: reveal?.revealedCount ?? 0 }, countdownTarget: null };
  }

  const immediateWinStartMs = getLiveDrawImmediateWinStartMs(drawnNumberCount);
  const winStartMs = announceWinStartElapsedMs ?? immediateWinStartMs;
  const winsAsSoonAsTheBallsSettle = winStartMs === immediateWinStartMs;

  if (hasWinningTicket && winsAsSoonAsTheBallsSettle && elapsedMs < winStartMs) {
    return { slot: { kind: 'drawing_numbers', revealedCount: drawnNumberCount }, countdownTarget: null };
  }

  if (hasWinningTicket && elapsedMs >= winStartMs && elapsedMs < winStartMs + ANNOUNCE_WIN_MS) {
    return { slot: { kind: 'announce_win' }, countdownTarget: null };
  }

  if (isAwaitingEvaluation) {
    return { slot: { kind: 'calculating_win' }, countdownTarget: null };
  }

  return { slot: { kind: 'complete' }, countdownTarget: null };
}

export { resolveLiveDrawStage };
export type { LiveDrawSlot, LiveDrawStage, ResolveLiveDrawStageInput };
